import { BUILD_INFO } from "../build-info";

export type UpdateListener = () => void;

/**
 * This page's build, as service workers name theirs (see vite.config.ts). `__codeshareTestApp`
 * lets E2E tests impersonate another build, as it does for peers.
 */
export function pageBuild(): string {
  const info = {
    ...BUILD_INFO,
    ...(globalThis as { __codeshareTestApp?: Partial<typeof BUILD_INFO> }).__codeshareTestApp,
  };
  return `${info.version}-${info.sha}-${info.builtAt}`;
}

/** The build a service worker serves; undefined if it doesn't say (older builds) in time. */
export function workerBuild(worker: ServiceWorker, timeoutMs = 1_000): Promise<string | undefined> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(undefined), timeoutMs);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      resolve(typeof event.data === "string" ? event.data : undefined);
    };
    worker.postMessage({ type: "VERSION" }, [channel.port2]);
  });
}

/** Resolves once a worker leaves the "installing" state (or after `timeoutMs`). */
function installed(worker: ServiceWorker, timeoutMs = 15_000): Promise<void> {
  if (worker.state !== "installing") return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    worker.addEventListener("statechange", () => {
      if (worker.state === "installing") return;
      clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * Registers the service worker (production builds only) and reports when a new version has been
 * downloaded and is waiting. Nothing changes for the user until `applyUpdate()` is called.
 */
export class AppUpdater {
  #registration: ServiceWorkerRegistration | undefined;
  readonly #listeners = new Set<UpdateListener>();
  #available = false;

  get updateAvailable(): boolean {
    return this.#available;
  }

  subscribe(listener: UpdateListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  async register(url = `${import.meta.env.BASE_URL}sw.js`): Promise<void> {
    if (!("serviceWorker" in navigator)) return;
    try {
      const registration = await navigator.serviceWorker.register(url, {
        scope: import.meta.env.BASE_URL,
      });
      this.#registration = registration;
      // First visit: hand over what already loaded so it's available offline too.
      const sendLoaded = () => {
        const urls = performance
          .getEntriesByType("resource")
          .map((entry) => entry.name)
          .filter((name) =>
            name.startsWith(`${location.origin}${import.meta.env.BASE_URL}assets/`),
          );
        navigator.serviceWorker.controller?.postMessage({ type: "CACHE_URLS", urls });
      };
      if (navigator.serviceWorker.controller) sendLoaded();
      else navigator.serviceWorker.addEventListener("controllerchange", sendLoaded, { once: true });
      if (registration.waiting && navigator.serviceWorker.controller) {
        void this.#offer(registration.waiting);
      }
      // The very first install isn't an update. Firefox can dispatch its `updatefound` late, after
      // that worker already activated and claimed the page, so remember which worker it was.
      const firstInstall = navigator.serviceWorker.controller ? null : registration.installing;
      registration.addEventListener("updatefound", () => {
        const installing = registration.installing;
        if (!installing || installing === firstInstall) return;
        installing.addEventListener("statechange", () => {
          // Installed and waiting while another version is active = an update is ready.
          if (
            installing.state === "installed" &&
            registration.waiting === installing &&
            registration.active
          ) {
            void this.#offer(installing);
          }
        });
      });
      // Check for new deploys periodically while the tab is open.
      setInterval(() => void this.checkForUpdate(), 30 * 60_000);
    } catch {
      // Service workers can be unavailable (private modes, file://); the app works without one.
    }
  }

  /** Asks the server for a newer service worker now (e.g. when a peer reports a newer build). */
  async checkForUpdate(): Promise<boolean> {
    if (!this.#registration) return false;
    try {
      await this.#registration.update();
    } catch {
      return false;
    }
    return this.#available || Boolean(this.#registration.installing || this.#registration.waiting);
  }

  /** Activates the waiting version and reloads into it (the URL fragment, i.e. the room, is kept). */
  async applyUpdate(): Promise<void> {
    // Still installing (e.g. right after checkForUpdate): wait, or the reload would load the new
    // build while its worker stays waiting, and then offer an update the page already has.
    const installing = this.#registration?.installing;
    if (installing) await installed(installing);
    const waiting = this.#registration?.waiting;
    if (!waiting) {
      location.reload();
      return;
    }
    navigator.serviceWorker.addEventListener("controllerchange", () => location.reload(), {
      once: true,
    });
    waiting.postMessage({ type: "SKIP_WAITING" });
  }

  /**
   * A new worker is waiting. Usually that's a newer build to offer. But if the page already runs
   * that build (it loaded from the network while the worker installed), just switch quietly.
   */
  async #offer(waiting: ServiceWorker): Promise<void> {
    if ((await workerBuild(waiting)) === pageBuild()) {
      waiting.postMessage({ type: "SKIP_WAITING" });
      return;
    }
    this.#setAvailable();
  }

  #setAvailable(): void {
    if (this.#available) return;
    this.#available = true;
    for (const listener of this.#listeners) listener();
  }
}

export const appUpdater = new AppUpdater();
